import "./StaffForm.scss";
import InfoContext from "pages/InfoContext";
import { FormattedMessage } from "react-intl";
import {
  TAG_TYPES,
  CONTRACT_TYPES,
  FILTERS_FOR_NAMES,
  MODAL_CONTENT,
  STAFF_PROFILES,
} from "constants/constants";
import Icon from "core/icon/Icon";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useRef, useState } from "react";
import {
  CONTRACTUAL_STATUS,
  FORM_INFO,
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
  CONTRACT_TYPE_OPTIONS,
  ROLE_CSE,
  ROLE_PLPM,
} from "constants/forms";
import { Controller } from "utils/Controller";
import {
  renderContractType,
  renderInputArrivalDate,
  renderInputBranch,
  renderInputCegid,
  renderInputCoefPos,
  renderInputContractStatus,
  renderInputEmail,
  renderInputProfile,
  renderInputManager,
  renderInputStartContract,
  renderInputEndContract,
  renderPersonalManagement,
  formatUsersOptions,
  renderInputReasonLeave,
  renderInputAssignedBranches,
} from "../CommonInputs";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { useParams } from "react-router-dom";
import _ from "lodash";
import {
  isAssignedBranchesRequiredHelper,
  rolesByProfileAutocomplete,
} from "utils/staffHelper";
import propTypes from "prop-types";
import { getCellErrorHelper, getTranslatedList, syncRole } from "utils/helper";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { ROLE_GP, ROLE_RGN_ASSIST, ROLE_RGP } from "access-roles/roles";

const CorporateInfoForm = ({
  readOnly = false,
  control,
  register,
  setValue,
  watch,
  errors,
  clearErrors,
}) => {
  const {
    branches,
    profileList,
    getOptions,
    lang,
    leavingReasons,
    personsByProfileList,
    userBySlug,
    syntecCoefStaffList,
    roleList,
    cellHighlight,
    userManager,
    contractualStatusList,
    apiErrorResponse,
    isCSE,
    setIsCSE,
    isPracticeLeader,
    setIsPracticeLeader,
    setStatus,
    setCode,
    setOpenModalIntent,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const branch_id = watch("branch_id", []);
  const profile_id = watch("profile_id", []);
  const previousProfileRef = useRef(profile_id[0]?.value);
  const manager_id = watch("manager_id", []);
  const gp_id = watch("gp_id", []);
  const leaving_reason_id = watch("leaving_reason_id", []);
  const role = watch("role", []);
  const contractual_status_id = watch("contractual_status_id", null);
  const syntec_coef_position_staff = watch("syntec_coef_position_staff", []);
  const assigned_branches = watch("assigned_branches", []);
  const contract_type = watch("contract_type", []);
  const start_date = watch("start_date", "");
  const slug = useParams();
  const [contractTypesList, setContractTypesList] = useState([]);
  const [syntecCoefStaffListFiltered, setSyntecCoefStaffListFiltered] =
    useState([]);

  const isAssignedBranchesRequired = isAssignedBranchesRequiredHelper({
    profile_id: profile_id[0]?.value?.toLowerCase(),
  });

  const staffProfileList = (profileList || []).filter((p) =>
    STAFF_PROFILES.includes(p.id?.toLowerCase())
  );

  useEffect(() => {
    const list =
      branch_id[0]?.label === "Davidson Canada INC"
        ? CONTRACT_TYPE_OPTIONS
        : CONTRACT_TYPE_OPTIONS.filter((e) => {
            return e.value !== "canada_temporary_contract";
          });
    setContractTypesList(list);
  }, [branch_id]);

  useEffect(() => {
    !_.isEmpty(start_date) && setValue("arrival_date", start_date);
  }, [start_date]);

  useEffect(() => {
    const newProfileValue = profile_id[0]?.value;
    const apiProfileValue = userBySlug?.profile_id;
    if (
      !_.isEmpty(slug) &&
      !_.isNil(apiProfileValue) &&
      String(apiProfileValue) === String(newProfileValue)
    ) {
      previousProfileRef.current = newProfileValue;
      return;
    }
    if (previousProfileRef.current !== newProfileValue) {
      if (!_.isNil(profile_id[0])) {
        const matchedRoles = rolesByProfileAutocomplete(
          newProfileValue.toLowerCase()
        );
        if (matchedRoles) {
          const matchedRolesAsStrings = matchedRoles.map((r) => r.value);
          const manualRoles = (role || []).filter(
            (r) => r === ROLE_CSE || r === ROLE_PLPM
          );
          setValue("role", [...matchedRolesAsStrings, ...manualRoles]);
        }
      } else {
        setValue("role", []);
      }
      previousProfileRef.current = newProfileValue;
    }
  }, [profile_id, setValue, slug, userBySlug]);

  useEffect(() => {
    if (!_.isEmpty(slug)) {
      setIsCSE(role?.includes(ROLE_CSE));
      setIsPracticeLeader(role?.includes(ROLE_PLPM));
    }
  }, [role, slug]);

  useEffect(() => {
    syncRole(isCSE, ROLE_CSE, role, setValue);
  }, [isCSE]);

  useEffect(() => {
    syncRole(isPracticeLeader, ROLE_PLPM, role, setValue);
  }, [isPracticeLeader]);

  const formatRolesToObjects = (rolesArray) =>
    Array.isArray(rolesArray)
      ? rolesArray.map((role) => ({ value: role, label: role }))
      : [];

  useEffect(() => {
    if (contract_type === CONTRACT_TYPES.CDI) {
      clearErrors("end_date");
    }
  }, [contract_type]);

  useEffect(() => {
    setValue("syntec_coef_position_staff", []);
    const filteredList = syntecCoefStaffList?.filter(
      (elm) => elm.contractual_status_id === contractual_status_id
    );
    setSyntecCoefStaffListFiltered(filteredList);
  }, [contractual_status_id]);

  const deleteRoleHandler = (val) => {
    const updatedRoles = watch("role").filter((r) => r !== val);
    setValue("role", updatedRoles);
  };

  const setRoleHandler = (val) => {
    const updatedStrings = val.map((item) => item.value);
    setValue("role", updatedStrings);
  };

  const renderInputRoles = () => (
    <Controller
      {...{
        control,
        register,
        name: "role",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSearchTag
            errorMessage={
              apiErrorResponse?.["role"] || errors?.["role"]?.message
            }
            label={STAFF_FORM_LABELS.role}
            limitChoice={FORM_INFO.roles.limitChoice}
            onDelete={(val) => deleteRoleHandler(val)}
            options={roleList || []}
            placeholder={STAFF_FORM_PLACEHOLDERS.role}
            popup={FORM_INFO.roles.popup}
            readOnly={readOnly}
            required
            selectedOptions={formatRolesToObjects(role)}
            setValue={(val) => setRoleHandler(val)}
            type={TAG_TYPES.ROLES}
            errors={errors?.["role"]}
            id="role"
            {...props}
          />
        ),
      }}
    />
  );

  const isDisabledCell = !readOnly && userBySlug ? "cell-disabled" : "";

  const formFieldsLineOnEdit = {
    line_1: [
      {
        render: renderInputEmail,
        label: STAFF_FORM_LABELS.email,
        placeholder: STAFF_FORM_PLACEHOLDERS.email,
        id: "email",
        errorMessage:
          apiErrorResponse?.["email"] ||
          (watch("email") && errors?.["email"]?.message),
        width: "20%",
        copyEmail: slug,
        setCode,
        setStatus,
      },
      {
        render: renderInputBranch,
        label: STAFF_FORM_LABELS.subsidiary,
        placeholder: STAFF_FORM_PLACEHOLDERS.branch_id,
        branches,
        value: branch_id,
        setValue,
        name: "branch_id",
        id: "branch_id",
        errors: errors?.["branch_id"] || apiErrorResponse?.["branch_id"],
        errorMessage:
          apiErrorResponse?.["branch_id"] || errors?.["branch_id"]?.message,
        width: "20%",
        className: isDisabledCell,
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
      {
        render: renderContractType,
        label: STAFF_FORM_LABELS.contract_type,
        placeholder: STAFF_FORM_PLACEHOLDERS.contract_type,
        options: contractTypesList,
        setValue,
        className: isDisabledCell,
        readOnly: userBySlug ? true : readOnly,
        id: "contract_type",
        errors: errors["contract_type"] || apiErrorResponse?.["contract_type"],
        errorMessage:
          apiErrorResponse?.["contract_type"] ||
          errors?.["contract_type"]?.message,
        width: "20%",
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
      {
        render: renderInputProfile,
        label: STAFF_FORM_LABELS.profile_id,
        placeholder: STAFF_FORM_PLACEHOLDERS.profile_id,
        profileList: staffProfileList,
        value: profile_id,
        setValue,
        id: "profile_id",
        errors: errors?.["profile_id"] || apiErrorResponse?.["profile_id"],
        errorMessage:
          apiErrorResponse?.["profile_id"] || errors?.["profile_id"]?.message,
        width: "20%",
      },
      {
        render: renderInputManager,
        errors: errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
        getNamesByFilters: getNamesByFilters,
        namesByFilters: namesByFilters,
        label: STAFF_FORM_LABELS.manager_id,
        personsByProfileList,
        placeholder: STAFF_FORM_PLACEHOLDERS.manager_id,
        value: manager_id,
        selectedOptions: userManager,
        setValue,
        className: isDisabledCell,
        readOnly: userBySlug ? true : readOnly,
        id: "manager_id",
        errorMessage:
          apiErrorResponse?.["manager_id"] || errors?.["manager_id"]?.message,
        width: "20%",
        modalIntent: MODAL_CONTENT.MANAGE_MANAGERS,
      },
    ],
    line_2: [
      {
        render: renderPersonalManagement,
        label: STAFF_FORM_LABELS.gp_id,
        errors: errors?.["gp_id"] || apiErrorResponse?.["gp_id"],
        errorMessage: apiErrorResponse?.["gp_id"] || errors?.["gp_id"]?.message,
        placeholder: STAFF_FORM_PLACEHOLDERS.gp_id,
        id: "gp_id",
        getOptions: (search) =>
          getNamesByFilters({
            role: [ROLE_GP, ROLE_RGP, ROLE_RGN_ASSIST],
            types: [FILTERS_FOR_NAMES.PERSON],
            search: search,
          }),
        options: formatUsersOptions({
          users: namesByFilters,
          type: TAG_TYPES.PEOPLE,
        }),
        value: gp_id,
        setValue,
      },
      {
        render: renderInputCegid,
        label: STAFF_FORM_LABELS.cegid_id,
        placeholder: STAFF_FORM_PLACEHOLDERS.cegid_id,
        id: "cegid_id",
      },
      {
        render: renderInputStartContract,
        label: STAFF_FORM_LABELS.start_date,
        placeholder: STAFF_FORM_PLACEHOLDERS.start_date,
        lang,
        className: isDisabledCell,
        readOnly: userBySlug ? true : readOnly,
        id: "start_date",
        setValue,
        value: watch("start_date"),
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
      {
        render: renderInputArrivalDate,
        label: STAFF_FORM_LABELS.arrival_date,
        placeholder: STAFF_FORM_PLACEHOLDERS.arrival_date,
        lang,
        className: isDisabledCell,
        readOnly: userBySlug ? true : readOnly,
        id: "arrival_date",
        setValue,
        value: watch("arrival_date"),
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
      {
        render: renderInputEndContract,
        label: STAFF_FORM_LABELS.end_date,
        placeholder: STAFF_FORM_PLACEHOLDERS.end_date,
        lang,
        className: isDisabledCell,
        readOnly: userBySlug ? true : readOnly,
        id: "end_date",
        contract_type,
        setValue,
        value: watch("end_date"),
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
      {
        render: renderInputReasonLeave,
        leavingReasons,
        lang,
        leaving_reason_id,
        setValue,
        watch,
        errors,
        control,
        register,
        label: STAFF_FORM_LABELS.leaving_reason_id,
        placeholder: STAFF_FORM_PLACEHOLDERS.leaving_reason_id,
        id: "leaving_reason_id",
        readOnly: true,
        className: isDisabledCell,
        modalIntent: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
      },
    ],
  };

  const formFieldsOnCreation = [
    {
      render: renderPersonalManagement,
      label: STAFF_FORM_LABELS.gp_id,
      getOptions: (search) =>
        getNamesByFilters({
          role: [ROLE_GP, ROLE_RGP, ROLE_RGN_ASSIST],
          types: [FILTERS_FOR_NAMES.PERSON],
          search: search,
        }),
      options: formatUsersOptions({
        users: namesByFilters,
        type: TAG_TYPES.PEOPLE,
      }),
      placeholder: STAFF_FORM_PLACEHOLDERS.gp_id,
      value: gp_id,
      setValue,
      id: "gp_id",
      errors: errors?.["gp_id"] || apiErrorResponse?.["gp_id"],
      errorMessage: apiErrorResponse?.["gp_id"] || errors?.["gp_id"]?.message,
    },
    {
      render: renderInputBranch,
      branches,
      label: STAFF_FORM_LABELS.subsidiary,
      placeholder: STAFF_FORM_PLACEHOLDERS.branch_id,
      value: branch_id,
      setValue,
      id: "branch_id",
      errors: errors?.["branch_id"] || apiErrorResponse?.["branch_id"],
      errorMessage:
        apiErrorResponse?.["branch_id"] || errors?.["branch_id"]?.message,
    },
    {
      render: renderContractType,
      label: STAFF_FORM_LABELS.contract_type,
      placeholder: STAFF_FORM_PLACEHOLDERS.contract_type,
      options: contractTypesList,
      setValue,
      id: "contract_type",
      errors: errors?.["contract_type"] || apiErrorResponse?.["contract_type"],
      errorMessage:
        apiErrorResponse?.["contract_type"] ||
        errors?.["contract_type"]?.message,
    },
    {
      render: renderInputProfile,
      profileList: staffProfileList,
      setValue,
      label: STAFF_FORM_LABELS.profile_id,
      lang,
      placeholder: STAFF_FORM_PLACEHOLDERS.profile_id,
      value: profile_id,
      id: "profile_id",
      errors: errors?.["profile_id"] || apiErrorResponse?.["profile_id"],
      errorMessage:
        apiErrorResponse?.["profile_id"] || errors?.["profile_id"]?.message,
    },
    {
      render: renderInputManager,
      getOptions,
      label: STAFF_FORM_LABELS.manager_id,
      personsByProfileList,
      placeholder: STAFF_FORM_PLACEHOLDERS.manager_id,
      value: manager_id,
      setValue,
      id: "manager_id",
      getNamesByFilters: getNamesByFilters,
      namesByFilters: namesByFilters,
      errors: errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
      errorMessage:
        apiErrorResponse?.["manager_id"] || errors?.["manager_id"]?.message,
    },
  ];

  return (
    <div className="section-3">
      {!_.isEmpty(slug) && (
        <div className="section-3">
          <div className="staff-corporate-info-form-id">
            <div className="left__div">
              <Typography
                className="title-3 violet text-uppercase"
                type="h2"
                label={
                  <FormattedMessage
                    id="corporate_info"
                    defaultMessage="Corporate info"
                  />
                }
              />
              <div className="line-1 ">
                {formFieldsLineOnEdit.line_1.map((field) => (
                  <div
                    className={`cell-padding ${getCellErrorHelper({
                      hasValue: watch(field.id),
                      hasError:
                        errors?.[field.id] || apiErrorResponse?.[field.id],
                      cellHighlight,
                    })} ${field.className || ""}`}
                    key={field.id}
                    style={{ width: field?.width, position: "relative" }}
                  >
                    {field.modalIntent && !readOnly && userBySlug && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (setOpenModalIntent) {
                            setOpenModalIntent(field.modalIntent);
                          }
                        }}
                        data-testid={`${field.id}-edit-icon`}
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          zIndex: 1,
                          background: "none",
                          border: "none",
                          padding: 4,
                          cursor: "pointer",
                          pointerEvents: "auto",
                        }}
                      >
                        <Icon
                          icon="pencil"
                          color="violet"
                          width={18}
                          height={18}
                          onClick={() => {}}
                        />
                      </button>
                    )}
                    {field.render({
                      control,
                      errors: errors?.[field.id],
                      label: field.label,
                      placeholder: field.placeholder,
                      readOnly,
                      register,
                      ...field,
                    })}
                  </div>
                ))}
              </div>
              <div className="line-2 ">
                {formFieldsLineOnEdit.line_2.map((field) => (
                  <div
                    className={`cell-padding
                       ${getCellErrorHelper({
                         hasValue: field.id === "cegid_id" || watch(field.id),
                         hasError:
                           errors?.[field.id] || apiErrorResponse?.[field.id],
                         cellHighlight,
                       })}

                    ${field.className || ""}`}
                    key={field.id}
                    style={{ position: "relative" }}
                  >
                    {field.modalIntent && !readOnly && userBySlug && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (setOpenModalIntent) {
                            setOpenModalIntent(field.modalIntent);
                          }
                        }}
                        data-testid={`${field.id}-edit-icon`}
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          zIndex: 1,
                          background: "none",
                          border: "none",
                          padding: 4,
                          cursor: "pointer",
                          pointerEvents: "auto",
                        }}
                      >
                        <Icon
                          icon="pencil"
                          color="violet"
                          width={18}
                          height={18}
                          onClick={() => {}}
                        />
                      </button>
                    )}
                    {field.render({
                      control,
                      errors: errors?.[field.id],
                      label: field.label,
                      placeholder: field.placeholder,
                      readOnly,
                      register,
                      ...field,
                    })}
                  </div>
                ))}
              </div>
              <div
                className={`cell-padding cell ${getCellErrorHelper({
                  hasValue: watch("role").length,
                  hasError: errors?.["role"],
                  cellHighlight,
                })}`}
              >
                {renderInputRoles()}
              </div>
            </div>
            {isAssignedBranchesRequired && (
              <>
                <br />
                <div className="right__div">
                  <Typography
                    className="title-3 violet text-uppercase"
                    type="h2"
                    label={
                      <FormattedMessage
                        id="achievable_business_on"
                        defaultMessage="Affaires réalisables sur"
                      />
                    }
                  />
                  <div
                    className={`cell-padding cell ${getCellErrorHelper({
                      hasValue: watch("assigned_branches").length,
                      hasError: apiErrorResponse?.assigned_branches,
                      cellHighlight,
                    })}`}
                  >
                    {renderInputAssignedBranches({
                      control,
                      register,
                      setValue,
                      assigned_branches,
                      branches,
                      readOnly,
                      isAssignedBranchesRequired,
                      apiErrorResponse,
                      errors,
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <br />
        </div>
      )}
      {_.isEmpty(slug) && (
        <>
          <div className="staff-corporate-info-form">
            <div className="left__div">
              <Typography
                className="title-3 violet text-uppercase"
                type="h2"
                label={
                  <FormattedMessage
                    id="corporate_info"
                    defaultMessage="Corporate info"
                  />
                }
              />
              <div className="line-1 ">
                {formFieldsOnCreation.map((field) => (
                  <div
                    className={`cell-padding ${getCellErrorHelper({
                      hasValue: watch(field.id)?.length,
                      hasError:
                        errors?.[field.id] || apiErrorResponse?.[field.id],
                      cellHighlight,
                    })}`}
                    key={field.id}
                  >
                    {field.render({
                      control,
                      errors: errors?.[field.id],
                      label: field.label,
                      placeholder: field.placeholder,
                      readOnly,
                      register,
                      ...field,
                    })}
                  </div>
                ))}
              </div>
              <div
                className={`line-2 ${
                  contractualStatusList
                    ?.find((elm) => elm?.id === contractual_status_id)
                    ?.en.toLowerCase() === CONTRACTUAL_STATUS.EXECUTIVE
                    ? "option-2"
                    : "option-1"
                }`}
              >
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("start_date")?.length,
                    cellHighlight,
                  })}`}
                >
                  {renderInputStartContract({
                    control,
                    errors,
                    label: STAFF_FORM_LABELS.start_date,
                    placeholder: STAFF_FORM_PLACEHOLDERS.start_date,
                    readOnly,
                    register,
                    lang,
                    setValue,
                    value: watch("start_date"),
                  })}
                </div>
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("arrival_date")?.length,
                    cellHighlight,
                  })}`}
                >
                  {renderInputArrivalDate({
                    control,
                    errors,
                    label: STAFF_FORM_LABELS.arrival_date,
                    placeholder: STAFF_FORM_PLACEHOLDERS.arrival_date,
                    readOnly,
                    register,
                    lang,
                    setValue,
                    value: watch("arrival_date"),
                  })}
                </div>
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue:
                      [
                        CONTRACT_TYPES.CDI,
                        CONTRACT_TYPES.REPRESENTATIVE,
                      ].includes(watch("contract_type")) ||
                      watch("end_date")?.length,
                    cellHighlight,
                  })}`}
                >
                  {renderInputEndContract({
                    control,
                    errors,
                    label: STAFF_FORM_LABELS.end_date,
                    readOnly,
                    register,
                    lang,
                    contract_type,
                    setValue,
                    value: watch("end_date"),
                  })}
                </div>
                <div className="cell-padding">
                  {renderInputReasonLeave({
                    control,
                    errors,
                    leavingReasons,
                    lang,
                    leaving_reason_id,
                    setValue,
                    watch,
                    readOnly,
                    register,
                  })}
                </div>
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("contractual_status_id"),
                    cellHighlight,
                  })}`}
                >
                  {renderInputContractStatus({
                    control,
                    errors,
                    label: STAFF_FORM_LABELS.contractual_status_id,
                    placeholder: STAFF_FORM_PLACEHOLDERS.contractual_status_id,
                    readOnly,
                    register,
                    setValue,
                    options: getTranslatedList({
                      lang,
                      list: contractualStatusList,
                    }),
                    errorMessage:
                      apiErrorResponse?.["contractual_status_id"] ||
                      errors?.["contractual_status_id"]?.message,
                  })}
                </div>
                {contractual_status_id &&
                  contractualStatusList?.find(
                    (elm) => elm?.en === CONTRACTUAL_STATUS.INTERNSHIP
                  )?.id !== contractual_status_id && (
                    <div
                      className={`cell-padding ${getCellErrorHelper({
                        hasValue: syntec_coef_position_staff?.length,
                        hasError:
                          errors?.["syntec_coef_position_staff"] ||
                          apiErrorResponse?.["syntec_coef_position_staff"],
                        cellHighlight,
                      })}`}
                    >
                      {renderInputCoefPos({
                        required: true,
                        control,
                        errors:
                          errors["syntec_coef_position_staff"] ||
                          apiErrorResponse?.["syntec_coef_position_staff"],
                        label: STAFF_FORM_LABELS.syntec_coef_position_staff,
                        placeholder:
                          STAFF_FORM_PLACEHOLDERS.syntec_coef_position_staff,
                        readOnly,
                        register,
                        name: "syntec_coef_position_staff",
                        setValue,
                        syntecCoef: syntecCoefStaffListFiltered,
                        value: syntec_coef_position_staff || [],
                        errorMessage:
                          apiErrorResponse?.["syntec_coef_position_staff"] ||
                          errors?.["syntec_coef_position_staff"]?.message,
                      })}
                    </div>
                  )}
              </div>
              <div className="left-role__div">
                <div
                  className={`cell-padding cell ${getCellErrorHelper({
                    hasValue: watch("role").length,
                    hasError: errors?.["role"] || apiErrorResponse?.["role"],
                    cellHighlight,
                  })}`}
                >
                  {renderInputRoles()}
                </div>
                <br />
              </div>
            </div>
            {isAssignedBranchesRequired && (
              <>
                <br />
                <div className="right__div">
                  <Typography
                    className="title-3 violet text-uppercase"
                    type="h2"
                    label={
                      <FormattedMessage
                        id="achievable_business_on"
                        defaultMessage="Affaires réalisables sur"
                      />
                    }
                  />
                  <div
                    className={`cell-padding cell ${getCellErrorHelper({
                      hasValue: watch("assigned_branches")?.length,
                      hasError: apiErrorResponse?.assigned_branches,
                      cellHighlight,
                    })}`}
                  >
                    {renderInputAssignedBranches({
                      control,
                      register,
                      setValue,
                      assigned_branches,
                      branches,
                      readOnly,
                      isAssignedBranchesRequired,
                      apiErrorResponse,
                      errors,
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <br />
        </>
      )}
    </div>
  );
};

export default CorporateInfoForm;

CorporateInfoForm.propTypes = {
  readOnly: propTypes.bool,
  control: propTypes.object,
  register: propTypes.func,
  setValue: propTypes.func,
  watch: propTypes.func,
  errors: propTypes.object,
  unregister: propTypes.func,
  clearErrors: propTypes.func,
};
